import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WorkItemCard } from '../lib/types';
import { WORK_ITEM_TYPE_GLYPH, WORK_ITEM_TYPE_LABELS, WORK_STAGE_LABELS } from '../lib/types';
import { BillingBadge, PriorityBadge } from '../components/Badges';
import { pipelineTone } from '../lib/types';
import { SlaBadge } from '../components/SlaBadge';
import { AssigneeStack } from '../components/Assignees';

interface Props {
  item: WorkItemCard;
  draggable: boolean;
  onOpen: (item: WorkItemCard) => void;
}

export function BoardCard({ item, draggable, onOpen }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !draggable,
    data: { statusId: item.statusId },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={isDragging ? 'board-card is-dragging' : 'board-card'}
      onClick={() => onOpen(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
    >
      <div className="row" style={{ gap: 6 }}>
        <span className="dim" title={WORK_ITEM_TYPE_LABELS[item.type]} aria-hidden style={{ fontSize: 11 }}>
          {WORK_ITEM_TYPE_GLYPH[item.type]}
        </span>
        <span className="key">{item.key}</span>
        {draggable && (
          <button
            type="button"
            className="board-card-drag"
            style={{ marginLeft: 'auto' }}
            aria-label={`Move ${item.key}`}
            onClick={(e) => e.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        )}
      </div>

      <div className="board-card-title">{item.title}</div>

      <div className="board-card-foot">
        <PriorityBadge priority={item.priority} />
        {item.points !== null && <span className="badge">{item.points} pts</span>}
        {item.dueDate && (
          <span className="badge" style={item.overdue ? { color: 'var(--accent)', borderColor: 'var(--accent-line)' } : undefined}>
            {item.overdue ? 'overdue ' : ''}
            {item.dueDate.slice(5)}
          </span>
        )}
        {/*
          * What is happening inside "in progress", and how far along it is — the stage says
          * which part of the job, the percentage says how much of it, and both without
          * opening anything.
          *
          * The number comes from the server so this card cannot disagree with the timeline
          * or the report about the same task. Nothing is shown when nobody has said.
          */}
        {item.stage && <span className={`badge stage-${item.stage}`}>{WORK_STAGE_LABELS[item.stage]}</span>}
        {item.percentComplete !== null && (
          <span className="badge tabular-nums" title="How far along this task is">
            {item.percentComplete}%
          </span>
        )}
        {item.blockedSince && <span className="badge badge-accent">blocked</span>}
        {item.closedAt && <span className="badge">closed</span>}
        <BillingBadge exception={item.billingException} />
        {item.reopenCount > 0 && <span className="badge badge-warn">↻{item.reopenCount}</span>}
        {item.merge && <MergeBadge merge={item.merge} />}
        {item.sla && <SlaBadge sla={item.sla} compact />}
        <span style={{ marginLeft: 'auto' }}>
          <AssigneeStack people={item.assignees} />
        </span>
      </div>
    </div>
  );
}

function MergeBadge({ merge }: { merge: NonNullable<WorkItemCard['merge']> }) {
  const tone = pipelineTone(merge.pipelineStatus);
  const cls =
    merge.state === 'merged'
      ? 'badge badge-ok'
      : tone === 'bad'
        ? 'badge badge-accent'
        : tone === 'warn'
          ? 'badge badge-warn'
          : 'badge badge-info';
  const title = `!${merge.iid} ${merge.state}${merge.pipelineStatus ? ` · pipeline ${merge.pipelineStatus}` : ''}`;
  return (
    <span className={cls} title={title}>
      ⑂ !{merge.iid}
      {merge.state === 'merged' ? ' ✓' : ''}
    </span>
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}
